import { FunctionComponent, ReactNode } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { LoopNavProviderContainerQuery } from "@framework/__generated__/relay/LoopNavProviderContainerQuery.graphql";
import {
  LoopNavMenuItem,
  LoopNavMenuSectionType,
  LoopNavComponentProps,
} from "@voxmedia/duet-context/context/loop-nav-context";
import LoopNavProvider from "@voxmedia/duet-context/providers/LoopNavProvider";

export interface LoopNavConfig {
  component?: React.ComponentType<LoopNavComponentProps>;
}

export interface Props {
  nodeId: string;
  children?: ReactNode;
  loopNavConfig?: LoopNavConfig;
}

const LoopNavProviderContainer: FunctionComponent<Props> = ({
  nodeId,
  loopNavConfig,
  children,
}) => {
  const { node } = useLazyLoadQuery<LoopNavProviderContainerQuery>(
    graphql`
      query LoopNavProviderContainerQuery($nodeId: ID!) {
        node(id: $nodeId) @required(action: THROW) {
          __typename
          ... on FeedContentResourceInterface {
            path
            loopNav {
              title
              items {
                listItemResourceId
                text
                permalink
                items {
                  listItemResourceId
                  permalink
                  text
                }
              }
            }
          }
          ... on CategoryResourceType {
            path
            loopNav {
              title
              items {
                listItemResourceId
                text
                permalink
                items {
                  listItemResourceId
                  permalink
                  text
                }
              }
            }
          }
          ... on VideoGameResourceType {
            path
            loopNav {
              title
              items {
                listItemResourceId
                text
                permalink
                items {
                  listItemResourceId
                  permalink
                  text
                }
              }
            }
          }
        }
      }
    `,
    { nodeId },
  );

  const loopNavData = node.loopNav
    ? {
        title: node.loopNav.title,
        sections: node.loopNav.items.map((section) => {
          let isSectionCurrent = false;
          const items = section.items.map((item) => {
            const itemResult: LoopNavMenuItem = {
              ...item,
            };
            if (
              item.listItemResourceId === nodeId ||
              (item.permalink && new URL(item.permalink).pathname === node.path)
            ) {
              itemResult.current = true;
              isSectionCurrent = true;
            }
            return itemResult;
          });
          const sectionResult: LoopNavMenuSectionType = {
            ...section,
            items,
            current: isSectionCurrent,
          };
          return sectionResult;
        }),
      }
    : null;

  return (
    <LoopNavProvider
      loopNavData={loopNavData}
      component={loopNavConfig?.component}
    >
      {children}
    </LoopNavProvider>
  );
};

export default LoopNavProviderContainer;
